import { computed, ref, toValue, watch } from 'vue'
import axios from 'axios'

/**
 * The "Report this clip" action shared by ClipsKebabMenu and ClipsMobileMenu. Both mount
 * once and stay mounted while the feed swipes, so the reported state has to follow the
 * clip it belongs to rather than the component.
 *
 * `clip` and `isUserLoggedIn` may be passed as getters (or refs) so the component's props
 * stay reactive through the factory call.
 */

// Flag::TYPE_INAPPROPRIATE_CONTENT. The menu entry is a single tap by design, so there is
// no type picker in front of it and every clip report arrives under this type.
export const REPORT_TYPE_INAPPROPRIATE_CONTENT = 1

export function useClipReport({ clip, routePrefix, isUserLoggedIn, type = REPORT_TYPE_INAPPROPRIATE_CONTENT }) {
    const isReporting = ref(false)
    const hasReported = ref(false)
    const reportError = ref('')

    const clipId = computed(() => toValue(clip)?.id ?? null)

    // Members only: the flag endpoint is registered on the members domain and attributes
    // the report to the signed-in account.
    const canReport = computed(
        () => !!toValue(isUserLoggedIn) && toValue(routePrefix) === 'members2' && !!clipId.value
    )

    const reportLabel = computed(() => (hasReported.value ? 'Reported' : 'Report this clip'))

    const reportClip = async () => {
        if (isReporting.value || hasReported.value || !clipId.value) return

        isReporting.value = true
        reportError.value = ''

        try {
            await axios.post(route('members2.flags.store'), {
                flaggable: 'verticalClip',
                flaggable_id: clipId.value,
                type,
            })

            hasReported.value = true
        } catch (error) {
            reportError.value = error?.response?.data?.message ?? 'Could not send that report.'
        } finally {
            isReporting.value = false
        }
    }

    watch(clipId, () => {
        hasReported.value = false
        reportError.value = ''
    })

    return {
        canReport,
        reportLabel,
        reportError,
        isReporting,
        hasReported,
        reportClip,
    }
}
